// src/composables/useNotifications.ts
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import socketService from '@/services/socketService';
import type { NotificationItem } from '@/store/modules/notifications';

export function useNotifications() {
  const store = useStore();
  const router = useRouter();

  // Safe state access with fallbacks
  const notifications = computed(() => {
    try {
      return store.state.notifications?.notifications || [];
    } catch (error) {
      return [];
    }
  });

  const badges = computed(() => {
    try {
      return (
        store.state.notifications?.badges || {
          messages: 0,
          follows: 0,
          products: 0,
          ratings: 0,
          total: 0,
        }
      );
    } catch (error) {
      return {
        messages: 0,
        follows: 0,
        products: 0,
        ratings: 0,
        total: 0,
      };
    }
  });

  const unreadNotifications = computed(() => {
    try {
      return store.getters['notifications/unreadNotifications'] || [];
    } catch (error) {
      return notifications.value.filter((n: NotificationItem) => !n.read);
    }
  });

  const unreadCount = computed(() => {
    try {
      return store.getters['notifications/unreadCount'] || 0;
    } catch (error) {
      return unreadNotifications.value.length;
    }
  });

  const isConnected = computed(() => {
    try {
      return store.state.notifications?.isConnected || false;
    } catch (error) {
      return false;
    }
  });

  const connectionError = computed(() => {
    try {
      return store.state.notifications?.connectionError || null;
    } catch (error) {
      return null;
    }
  });

  const totalBadgeCount = computed(() => {
    try {
      return store.getters['notifications/totalBadgeCount'] || 0;
    } catch (error) {
      return badges.value.total || 0;
    }
  });

  // Specific badge counts
  const hasUnreadMessages = computed(() => {
    try {
      return store.getters['notifications/hasUnreadMessages'] || false;
    } catch (error) {
      return badges.value.messages > 0;
    }
  });

  const hasUnreadFollows = computed(() => {
    try {
      return store.getters['notifications/hasUnreadFollows'] || false;
    } catch (error) {
      return badges.value.follows > 0;
    }
  });

  const hasUnreadProducts = computed(() => {
    try {
      return store.getters['notifications/hasUnreadProducts'] || false;
    } catch (error) {
      return badges.value.products > 0;
    }
  });

  const hasUnreadRatings = computed(() => {
    try {
      return store.getters['notifications/hasUnreadRatings'] || false;
    } catch (error) {
      return badges.value.ratings > 0;
    }
  });

  // Connection management
  const connectSocket = async () => {
    try {
      const token =
        localStorage.getItem('token') || (store.state as any).auth?.token;

      if (!token) {
        return;
      }

      await socketService.connect();
    } catch (error) {
      console.error('[useNotifications] Socket connection failed:', error);
    }
  };

  const disconnectSocket = () => {
    socketService.disconnect();
  };

  const forceReconnect = async () => {
    try {
      await socketService.forceReconnect();
    } catch (error) {
      console.error(
        '[useNotifications] Socket force reconnection failed:',
        error
      );
    }
  };

  // Notification management
  const markAsRead = (notificationId: string) => {
    try {
      store.dispatch('notifications/markAsReadAPI', notificationId);
    } catch (error) {
      console.error('[useNotifications] Error marking as read:', error);
    }
  };

  const markAllAsRead = () => {
    try {
      store.dispatch('notifications/markAllAsReadAPI');
    } catch (error) {
      console.error('[useNotifications] Error marking all as read:', error);
    }
  };

  const removeNotification = (notificationId: string) => {
    try {
      store.dispatch('notifications/removeNotification', notificationId);
    } catch (error) {
      console.error('[useNotifications] Error removing notification:', error);
    }
  };

  const clearAllNotifications = () => {
    try {
      store.dispatch('notifications/clearAllNotifications');
    } catch (error) {
      console.error(
        '[useNotifications] Error clearing all notifications:',
        error
      );
    }
  };

  // Navigation handling based on notification type
  const handleNotificationClick = async (notification: NotificationItem) => {
    try {
      // Close notification dropdown
      document.dispatchEvent(new CustomEvent('closeNotificationDropdown'));

      // Mark as read first (non-blocking)
      if (!notification.read) {
        store.dispatch('notifications/markAsReadAPI', notification.id);
      }

      // Navigate based on notification type
      switch (notification.type) {
        case 'new_message':
          router.push('/messages');
          break;

        case 'user_follow':
          const followedUserId =
            notification.data?.followed?.id || notification.data?.followedId;

          if (followedUserId) {
            router.push(`/profile/${followedUserId}`);
          } else {
            const currentUserId = store.state.auth?.user?.id;
            if (currentUserId) {
              router.push(`/profile/${currentUserId}`);
            }
          }
          break;

        case 'product_follow':
          const productSlug =
            notification.data?.product?.slug || notification.product?.slug;
          if (productSlug) {
            router.push(`/product/${productSlug}`);
          } else {
            router.push('/my-listings');
          }
          break;

        case 'product_expired':
          router.push('/my-listings?tab=inactive');
          break;

        case 'new_rating':
          router.push('/ratings');
          break;

        default:
          console.warn(
            '[useNotifications] Unknown notification type:',
            notification.type
          );
      }
    } catch (error) {
      console.error(
        '[useNotifications] Error handling notification click:',
        error
      );
    }
  };

  // Format time helper
  const formatTime = (date: Date | string): string => {
    try {
      const notificationDate = typeof date === 'string' ? new Date(date) : date;
      const now = new Date();
      const diffInSeconds = Math.floor(
        (now.getTime() - notificationDate.getTime()) / 1000
      );

      if (diffInSeconds < 60) {
        return 'Upravo';
      } else if (diffInSeconds < 3600) {
        const minutes = Math.floor(diffInSeconds / 60);
        return `Pre ${minutes} min`;
      } else if (diffInSeconds < 86400) {
        const hours = Math.floor(diffInSeconds / 3600);
        return `Pre ${hours} h`;
      } else {
        const days = Math.floor(diffInSeconds / 86400);
        if (days === 1) {
          return 'Juče';
        } else if (days < 7) {
          return `Pre ${days} dana`;
        } else {
          return notificationDate.toLocaleDateString('sr-RS');
        }
      }
    } catch (error) {
      return 'Nepoznato vreme';
    }
  };

  // Get notification title based on type
  const getNotificationTitle = (notification: NotificationItem): string => {
    try {
      switch (notification.type) {
        case 'new_message':
          const senderUsername =
            notification.data?.sender?.username ||
            notification.sender?.username ||
            'Nepoznat korisnik';
          return `${senderUsername} vam je poslao poruku`;

        case 'user_follow':
          return `Imate novog pratioca!`;

        case 'product_follow':
          return 'Vaš proizvod je zapraćen';

        case 'product_expired':
          return 'Proizvod je istekao';

        case 'new_rating':
          const raterUsername =
            notification.data?.rater?.username ||
            (notification as any).rater?.username ||
            'Nepoznat korisnik';
          return `${raterUsername} vas je ocenio`;

        default:
          return 'Nova notifikacija';
      }
    } catch (error) {
      return 'Nova notifikacija';
    }
  };

  // Get notification message based on type
  const getNotificationMessage = (notification: NotificationItem): string => {
    try {
      switch (notification.type) {
        case 'new_message':
          const messageContent =
            notification.data?.message?.content ||
            notification.message?.content ||
            'Kliknite da vidite poruku';
          return messageContent.substring(0, 100);

        case 'user_follow':
          return 'Kliknite da vidite svoj profil.';

        case 'product_follow':
          const productName = notification.data?.product?.name || 'proizvod';
          return `Vaš oglas "${productName}" ima novog pratioca`;

        case 'product_expired':
          const expiredProductName =
            notification.data?.product?.name ||
            notification.product?.name ||
            'Nepoznat proizvod';
          return `Proizvod "${expiredProductName}" je prešao u neaktivne`;

        case 'new_rating':
          const rating =
            notification.data?.rating || (notification as any).rating;
          return `Ocena: ${rating?.isLike ? '👍 Pozitivna' : '👎 Negativna'}${
            rating?.comment ? ` - "${rating.comment}"` : ''
          }`;

        default:
          return 'Kliknite za više detalja';
      }
    } catch (error) {
      return 'Kliknite za više detalja';
    }
  };

  // Get icon class for notification type
  const getNotificationIconClass = (type: string): string => {
    const classes = {
      new_message: 'bg-blue-100 text-blue-600',
      user_follow: 'bg-green-100 text-green-600',
      product_follow: 'bg-pink-100 text-pink-600',
      product_expired: 'bg-orange-100 text-orange-600',
      new_rating: 'bg-yellow-100 text-yellow-600',
    };
    return classes[type as keyof typeof classes] || 'bg-gray-100 text-gray-600';
  };

  // Lifecycle management
  const initializeNotifications = async () => {
    try {
      if (!store.state.notifications) {
        return;
      }

      const isAuthenticated =
        store.getters['auth/isAuthenticated'] || store.state.auth?.isLoggedIn;
      const token = store.state.auth?.token || localStorage.getItem('token');

      if (isAuthenticated && token) {
        await connectSocket();
      }
    } catch (error) {
      console.error(
        '[useNotifications] Error initializing notifications:',
        error
      );
    }
  };

  const cleanupNotifications = () => {
    try {
      if (store.state.notifications) {
        store.dispatch('notifications/saveNotifications');
      }
      disconnectSocket();
    } catch (error) {
      console.error('[useNotifications] Error during cleanup:', error);
    }
  };

  return {
    // State
    notifications,
    badges,
    unreadNotifications,
    unreadCount,
    totalBadgeCount,
    isConnected,
    connectionError,

    // Computed flags
    hasUnreadMessages,
    hasUnreadFollows,
    hasUnreadProducts,
    hasUnreadRatings,

    // Actions
    markAsRead,
    markAllAsRead,
    removeNotification,
    clearAllNotifications,
    handleNotificationClick,

    // Connection management
    connectSocket,
    disconnectSocket,
    forceReconnect,

    // Utilities
    formatTime,
    getNotificationTitle,
    getNotificationMessage,
    getNotificationIconClass,

    // Lifecycle
    initializeNotifications,
    cleanupNotifications,
  };
}
